/**
 * Encrypts all localStorage / sessionStorage values at rest (AES).
 * Patched at app bootstrap so existing getItem/setItem calls stay unchanged.
 *
 * Note: Key material ships with the client bundle — this hides data from casual
 * DevTools viewing, not from a determined attacker. Set REACT_APP_STORAGE_SECRET in production.
 */
import CryptoJS from "crypto-js";

export const STORAGE_ENC_PREFIX = "MTENC:v1:";

const valueCache = {
  local: new Map<string, string>(),
  session: new Map<string, string>(),
};

let patchInstalled = false;

function getPassphrase(): string {
  const secret =
    process.env?.REACT_APP_STORAGE_SECRET ?? "myTara@2024-storage-pepper";

  const origin =
    globalThis.window === undefined
      ? "mytara-local"
      : globalThis.window.location.origin;

  return `${secret}::${origin}`;
}

export function encryptValue(plain: string): string {
  const cipher = CryptoJS.AES.encrypt(plain, getPassphrase()).toString();
  return `${STORAGE_ENC_PREFIX}${cipher}`;
}

export function decryptValue(stored: string | null): string | null {
  if (stored == null) return null;

  if (!stored.startsWith(STORAGE_ENC_PREFIX)) {
    return stored;
  }

  try {
    const cipher = stored.slice(STORAGE_ENC_PREFIX.length);
    const bytes = CryptoJS.AES.decrypt(cipher, getPassphrase());
    const text = bytes.toString(CryptoJS.enc.Utf8);
    return text.length > 0 ? text : null;
  } catch {
    return null;
  }
}

function isEncryptedPayload(value: string): boolean {
  return value.startsWith(STORAGE_ENC_PREFIX);
}

type NativeStorageOps = {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => void;
  removeItem: (key: string) => void;
  clear: () => void;
  key: (index: number) => string | null;
};

function migrateStorage(
  storage: Storage,
  cache: Map<string, string>,
  native: NativeStorageOps
): void {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const k = native.key(i);
    if (k) keys.push(k);
  }

  for (const key of keys) {
    const raw = native.getItem(key);
    if (raw == null) continue;

    if (isEncryptedPayload(raw)) {
      const plain = decryptValue(raw);
      if (plain != null) cache.set(key, plain);
      continue;
    }

    cache.set(key, raw);
    try {
      native.setItem(key, encryptValue(raw));
    } catch {
      // Quota or private mode — keep plaintext in cache only
    }
  }
}

function patchStorageInstance(storage: Storage, cache: Map<string, string>): void {
  const native: NativeStorageOps = {
    getItem: storage.getItem.bind(storage),
    setItem: storage.setItem.bind(storage),
    removeItem: storage.removeItem.bind(storage),
    clear: storage.clear.bind(storage),
    key: storage.key.bind(storage),
  };

  migrateStorage(storage, cache, native);

  storage.getItem = function getItem(key: string): string | null {
    if (cache.has(key)) {
      return cache.get(key) ?? null;
    }

    const raw = native.getItem(key);
    if (raw == null) return null;

    const plain = decryptValue(raw);
    if (plain != null) {
      cache.set(key, plain);
    }
    return plain;
  };

  storage.setItem = function setItem(key: string, value: string): void {
    const str = String(value);
    cache.set(key, str);
    native.setItem(key, encryptValue(str));
  };

  storage.removeItem = function removeItem(key: string): void {
    cache.delete(key);
    native.removeItem(key);
  };

  storage.clear = function clear(): void {
    cache.clear();
    native.clear();
  };
}

/** Install AES encryption on browser Storage APIs (idempotent). */
export function installSecureStoragePatch(): void {
  if (patchInstalled) return;
  if (globalThis.window === undefined) return;

  patchStorageInstance(globalThis.window.localStorage, valueCache.local);
  patchStorageInstance(globalThis.window.sessionStorage, valueCache.session);

  patchInstalled = true;
}

/** Clear in-memory decrypted cache (e.g. after logout in tests). */
export function clearSecureStorageMemoryCache(): void {
  valueCache.local.clear();
  valueCache.session.clear();
}
